const IV_BYTES = 12
const WRAP_SALT_BYTES = 16
const VAULT_KEY_BYTES = 32

/**
 * Coerce any Uint8Array to one backed by a standalone ArrayBuffer.
 *
 * TypeScript 5.7+ makes Uint8Array generic: `Uint8Array<ArrayBufferLike>`.
 * Web Crypto expects `BufferSource` backed by plain `ArrayBuffer`, not
 * `SharedArrayBuffer`.  Rather than scattering `as` casts across every
 * call-site, we normalize once at the boundary.  If the source is already
 * backed by a sole-owner ArrayBuffer (the common case) this is nearly free;
 * if it ever truly comes from a SharedArrayBuffer the copy keeps us correct.
 */
function toAB(src: Uint8Array): Uint8Array<ArrayBuffer> {
    if (src.buffer instanceof ArrayBuffer && src.byteOffset === 0 && src.byteLength === src.buffer.byteLength) {
        return src as Uint8Array<ArrayBuffer>
    }
    return new Uint8Array(src) as Uint8Array<ArrayBuffer>
}

export interface WrappedVaultKey {
    vaultKeyCiphertext: ArrayBuffer
    keyWrapSalt: ArrayBuffer
    keyWrapIv: ArrayBuffer
}

export interface VaultKeyMaterial {
    key: CryptoKey
    raw: Uint8Array
}

async function deriveWrapKey(pin: string, salt: Uint8Array): Promise<CryptoKey> {
    const material = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, ['deriveKey'])
    return crypto.subtle.deriveKey(
        { name: 'PBKDF2', hash: 'SHA-256', salt: toAB(salt), iterations: 210_000 },
        material,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
    )
}

export async function importVaultKey(raw: Uint8Array): Promise<CryptoKey> {
    return crypto.subtle.importKey('raw', toAB(raw), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
}

export async function generateVaultKeyMaterial(): Promise<VaultKeyMaterial> {
    const raw = crypto.getRandomValues(new Uint8Array(VAULT_KEY_BYTES))
    const key = await importVaultKey(raw)
    return { key, raw }
}

export async function encryptAesGcm(key: CryptoKey, iv: Uint8Array, plaintext: Uint8Array, aad?: Uint8Array): Promise<ArrayBuffer> {
    return crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: toAB(iv), ...(aad ? { additionalData: toAB(aad) } : {}) },
        key,
        toAB(plaintext),
    )
}

export async function decryptAesGcm(key: CryptoKey, iv: Uint8Array, ciphertext: ArrayBuffer, aad?: Uint8Array): Promise<ArrayBuffer> {
    return crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: toAB(iv), ...(aad ? { additionalData: toAB(aad) } : {}) },
        key,
        ciphertext,
    )
}

export async function wrapVaultKeyWithPin(rawVaultKey: Uint8Array, pin: string): Promise<WrappedVaultKey> {
    const wrapSalt = crypto.getRandomValues(new Uint8Array(WRAP_SALT_BYTES))
    const wrapIv = crypto.getRandomValues(new Uint8Array(IV_BYTES))
    const wrapKey = await deriveWrapKey(pin, wrapSalt)
    const vaultKeyCiphertext = await encryptAesGcm(wrapKey, wrapIv, rawVaultKey)

    return {
        vaultKeyCiphertext,
        keyWrapSalt: wrapSalt.buffer as ArrayBuffer,
        keyWrapIv: wrapIv.buffer as ArrayBuffer,
    }
}

export async function unwrapVaultKeyWithPin(wrapped: WrappedVaultKey, pin: string): Promise<Uint8Array> {
    const wrapKey = await deriveWrapKey(pin, new Uint8Array(wrapped.keyWrapSalt))
    const rawVaultKey = await decryptAesGcm(wrapKey, new Uint8Array(wrapped.keyWrapIv), wrapped.vaultKeyCiphertext)
    return new Uint8Array(rawVaultKey)
}

export function randomIv(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(IV_BYTES))
}

export function zeroize(buffer: Uint8Array): void {
    buffer.fill(0)
}

// ─── PRF-based vault key wrapping (§4.1 WebAuthn PRF Extension) ──────────
const PRF_HKDF_INFO = new TextEncoder().encode('secureblob-vault-wrap')

/**
 * Derive a wrapping key from WebAuthn PRF output using HKDF-SHA256.
 * PRF output is hardware-bound secret — never stored, never leaves the authenticator.
 */
async function deriveWrapKeyFromPrf(prfOutput: Uint8Array, salt: Uint8Array): Promise<CryptoKey> {
    const material = await crypto.subtle.importKey('raw', toAB(prfOutput), 'HKDF', false, ['deriveKey'])
    return crypto.subtle.deriveKey(
        { name: 'HKDF', hash: 'SHA-256', salt: toAB(salt), info: toAB(PRF_HKDF_INFO) },
        material,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
    )
}

export async function wrapVaultKeyWithPrf(rawVaultKey: Uint8Array, prfOutput: Uint8Array): Promise<WrappedVaultKey> {
    const wrapSalt = crypto.getRandomValues(new Uint8Array(WRAP_SALT_BYTES))
    const wrapIv = crypto.getRandomValues(new Uint8Array(IV_BYTES))
    const wrapKey = await deriveWrapKeyFromPrf(prfOutput, wrapSalt)
    const vaultKeyCiphertext = await encryptAesGcm(wrapKey, wrapIv, rawVaultKey)

    return {
        vaultKeyCiphertext,
        keyWrapSalt: wrapSalt.buffer as ArrayBuffer,
        keyWrapIv: wrapIv.buffer as ArrayBuffer,
    }
}

export async function unwrapVaultKeyWithPrf(wrapped: WrappedVaultKey, prfOutput: Uint8Array): Promise<Uint8Array> {
    const wrapKey = await deriveWrapKeyFromPrf(prfOutput, new Uint8Array(wrapped.keyWrapSalt))
    const rawVaultKey = await decryptAesGcm(wrapKey, new Uint8Array(wrapped.keyWrapIv), wrapped.vaultKeyCiphertext)
    return new Uint8Array(rawVaultKey)
}
